import { ReactElement, MouseEvent, useMemo, useCallback } from 'react';
import { Stack, Button } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartLine as ChartLineIcon,
  faStar as StarIcon,
  faBorderNone as BorderNoneIcon,
} from '@fortawesome/free-solid-svg-icons';
import { Chart as ChartJS, ArcElement } from 'chart.js';
import { Doughnut as DoughnutChart } from 'react-chartjs-2';

import currentTheme from '~config/theme';
import { toAbsoluteUrl } from '~utils/assetsHelpers';

import { DashboardVariantV2GiverStatusInfoWidgetCard } from '../../SubComponents';

import { useDashboardVariantV2GiverContext } from '../../../Context';
import { DashboardVariantV2GiverContextTabId } from '../../../types';

import { getChartProps } from './utils';

ChartJS.register(ArcElement);

const WIDGET_TAB_ID: DashboardVariantV2GiverContextTabId = 'finalized';
const WIDGET_ACCENT_COLOR = currentTheme.palette.success.main;

const DashboardVariantV2GiverFinalizedWidget = (): ReactElement => {
  const {
    activeTab: { getIsActiveTab, setActiveTab },
    statistics: { data: statistics },
    dialogs: {
      reports: { toggleOpen: toogleOpenDialogReports },
      userSatisfaction: { toggleOpen: toogleOpenDialogUserSatisfaction },
      heatmap: { toggleOpen: toogleOpenDialogHeatmap },
    },
  } = useDashboardVariantV2GiverContext();

  const isSelected = useMemo(
    () => getIsActiveTab(WIDGET_TAB_ID),
    [getIsActiveTab],
  );

  const handleSelectedTab = useCallback(() => {
    setActiveTab(WIDGET_TAB_ID);
  }, [setActiveTab]);

  const handleOpenDialog = useCallback(
    (toogleOpenDialog: (nextValue?: boolean) => void) =>
      (event: MouseEvent<HTMLElement>) => {
        event.stopPropagation();

        toogleOpenDialog(true);
      },
    [],
  );

  return (
    <DashboardVariantV2GiverStatusInfoWidgetCard
      accentColor={WIDGET_ACCENT_COLOR}
      isSelected={isSelected}
      onSelected={handleSelectedTab}
      header={{
        info: (
          <DoughnutChart
            {...getChartProps(
              statistics.completed.totalCompletedtems,
              statistics.completed.totalWithoutIssuesItems,
              statistics.completed.totalWithIssuesItems,
            )}
          />
        ),
        statistics: {
          current: statistics.completed.totalCompletedtems,
          total: statistics.totalItems,
        },
        icon: {
          src: toAbsoluteUrl('/icons/icon_section_completed.svg'),
          alt: 'Section Completed Icon',
        },
        title: {
          text: 'FINALIZADOS',
          tooltip: 'Pedidos cumplidos con y sin incidencias',
        },
      }}
      content={
        <Stack direction="row" spacing={1} justifyContent="space-between">
          <Button
            variant="text"
            color="secondary"
            startIcon={
              <FontAwesomeIcon
                icon={ChartLineIcon}
                color={currentTheme.palette.primary.main}
              />
            }
            onClick={handleOpenDialog(toogleOpenDialogReports)}
          >
            Reportes
          </Button>

          <Button
            variant="text"
            color="secondary"
            startIcon={
              <FontAwesomeIcon
                icon={StarIcon}
                color={currentTheme.palette.primary.main}
              />
            }
            onClick={handleOpenDialog(toogleOpenDialogUserSatisfaction)}
          >
            Calificaciones
          </Button>

          <Button
            variant="text"
            color="secondary"
            startIcon={
              <FontAwesomeIcon
                icon={BorderNoneIcon}
                color={currentTheme.palette.primary.main}
              />
            }
            onClick={handleOpenDialog(toogleOpenDialogHeatmap)}
          >
            Mapa de calor
          </Button>
        </Stack>
      }
    />
  );
};

export default DashboardVariantV2GiverFinalizedWidget;
